import { ArrowLeft, BookOpenCheck, CheckCircle2, ExternalLink, FileQuestion, FolderGit2, GraduationCap, Newspaper, PlayCircle, Route, Sparkles, Target, Trophy } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { recordStudentGrowthEvent } from '../api.js'
import {
  getLearnedResources,
  getLearningResource,
  markResourceLearned,
} from '../student-learning-resources.js'
import { useState } from 'react'

const sectionIcons = {
  order: Route,
  videos: PlayCircle,
  docs: ExternalLink,
  practice: Target,
  openSource: FolderGit2,
  courses: GraduationCap,
  articles: Newspaper,
  interviews: FileQuestion,
  projects: Trophy,
}

function ResourceList({ icon, items, title }) {
  const Icon = icon
  return (
    <section className="student-resource-section">
      <h2><Icon size={18} /> {title}</h2>
      <ul>
        {items.map((item) => (
          <li key={typeof item === 'string' ? item : item.label}>
            {typeof item === 'string'
              ? item
              : <a href={item.url} target="_blank" rel="noreferrer">{item.label} <ExternalLink size={13} /></a>}
          </li>
        ))}
      </ul>
    </section>
  )
}

function StudentResourceDetailPage() {
  const { resourceId } = useParams()
  const resource = getLearningResource(resourceId)
  const [learned, setLearned] = useState(() => getLearnedResources().includes(resource?.id))
  const [notice, setNotice] = useState('')

  async function completeResource() {
    if (!resource) return
    markResourceLearned(resource.id)
    setLearned(true)
    setNotice('已标记为已学习，成长中心会优先显示你最近推进的资源任务')
    try {
      await recordStudentGrowthEvent({
        eventType: 'learning_resource_completed',
        title: `完成资源学习{resource.title}`,
        description: resource.next,
        metadata: { resourceId: resource.id, category: resource.category },
      })
    } catch {
      setNotice('已本地标记为已学习，登录后可同步到成长时间轴')
    }
  }

  if (!resource) {
    return (
      <main className="page-container student-resource-page">
        <section className="student-resource-empty">
          <span><BookOpenCheck size={17} /> 学习资源</span>
          <h1>还没有找到这个资源</h1>
          <p>可以返回资源中心，先Spring Boot、Redis、蓝桥杯、软考等模板开始</p>
          <Link className="primary-button" to="/resource">返回资源中心</Link>
        </section>
      </main>
    )
  }

  return (
    <main className="page-container student-resource-page">
      <div className="student-project-rec-topbar">
        <Link className="secondary-button" to="/resource"><ArrowLeft size={16} /> 返回资源中心</Link>
        <Link className="secondary-button" to="/growth">返回成长中心</Link>
      </div>

      <section className="student-resource-detail-hero">
        <span><BookOpenCheck size={17} /> {resource.category}</span>
        <h1>{resource.title}</h1>
        <p>{resource.intro}</p>
        <div className="student-friendly-tags">
          <span>推荐学习</span>
          <span>可写简</span>
          <span>规则模板</span>
        </div>
      </section>

      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      <section className="student-resource-layout">
        <aside className="student-resource-toc">
          <strong>学习页面</strong>
          {['为什么要', '推荐学习顺序', '免费视频', '官方文档', '练习网站', '开源项', '课程', '文章', '面试', '实战项目', '下一'].map((item) => (
            <span key={item}>{item}</span>
          ))}
          <button className="primary-button" type="button" onClick={completeResource} disabled={learned}>
            <CheckCircle2 size={16} /> {learned ? 'Learned' : 'Mark learned'}
          </button>
        </aside>

        <div className="student-resource-content">
          <section className="student-resource-section is-highlight">
            <h2><Sparkles size={18} /> 为什么要</h2>
            <p>{resource.why}</p>
          </section>
          <ResourceList icon={sectionIcons.order} items={resource.order} title="推荐学习顺序" />
          <ResourceList icon={sectionIcons.videos} items={resource.videos} title="推荐免费视频" />
          <ResourceList icon={sectionIcons.docs} items={resource.docs} title="推荐官方文档" />
          <ResourceList icon={sectionIcons.practice} items={resource.practice} title="推荐练习网站" />
          <ResourceList icon={sectionIcons.openSource} items={resource.openSource} title="Open source projects" />
          <ResourceList icon={sectionIcons.courses} items={resource.courses} title="推荐课程" />
          <ResourceList icon={sectionIcons.articles} items={resource.articles} title="推荐文章" />
          <ResourceList icon={sectionIcons.interviews} items={resource.interviews} title="Interview resources" />
          <ResourceList icon={sectionIcons.projects} items={resource.projects} title="推荐实战项目" />
          <section className="student-resource-section is-highlight">
            <h2><Target size={18} /> 推荐下一</h2>
            <p>{resource.next}</p>
          </section>
        </div>
      </section>
    </main>
  )
}

export default StudentResourceDetailPage
